import React from 'react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { useTranslation } from 'react-i18next';
import { i18nKeys } from '@/i18n/i18nKeyGen';
import type { AppTheme } from '@/theme/theme';
const slide = keyframes`
  0% { transform: translateX(-100%); }
  100% { transform: translateX(300%); }
`;

const LoadingOverlay = styled.div<{ isVisible: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.7);
  display: ${props => (props.isVisible ? 'flex' : 'none')};
  justify-content: center;
  align-items: center;
  z-index: 9999;
  backdrop-filter: blur(4px);
  pointer-events: all;
  cursor: wait;
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 360px;
`;

const ProgressWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ProgressTrack = styled.div`
  width: 100%;
  height: 6px;
  background-color: rgba(255, 255, 255, 0.2);
  border-radius: 3px;
  overflow: hidden;
  position: relative;
`;

const IndeterminateBar = styled.div`
  position: absolute;
  top: 0;
  height: 100%;
  width: 35%;
  border-radius: 3px;
  background-color: ${({ theme }) => (theme as AppTheme).colors.control.splitBar.select};
  animation: ${slide} 1.4s cubic-bezier(0.4, 0, 0.2, 1) infinite;
`;

const DeterminateFill = styled.div<{ value: number }>`
  height: 100%;
  border-radius: 3px;
  background-color: ${({ theme }) => (theme as AppTheme).colors.control.splitBar.select};
  width: ${({ value }) => `${value}%`};
  transition: width 0.3s ease;
`;

const LoadingText = styled.div`
  color: rgba(255, 255, 255, 1);
  font-size: 16px;
  margin-bottom: 16px;
  text-align: center;
  font-weight: 500;
  line-height: 1.6;
  width: 100%;
`;

const LoadingSubText = styled.div`
  color: rgba(255, 255, 255, 0.8);
  font-size: 14px;
  margin-top: 10px;
  text-align: center;
`;

const CancelButton = styled.button`
  margin-top: 24px;
  padding: 10px 24px;
  background-color: ${({ theme }) => (theme as AppTheme).colors.status.danger.stroke.border};
  color: rgba(255, 255, 255, 1);
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  pointer-events: auto;

  &:hover {
    opacity: 0.9;
    transform: translateY(-1px);
    box-shadow: 0 4px 8px ${({ theme }) => (theme as AppTheme).colors.border.shadow.md};
  }

  &:active {
    transform: translateY(0);
  }
`;

const WarningText = styled.div`
  color: ${({ theme }) => (theme as AppTheme).colors.status.caution.stroke.border};
  font-size: 12px;
  margin-top: 8px;
  text-align: center;
  font-weight: 500;
`;

interface GlobalLoadingProps {
  isVisible: boolean;
  message?: string;
  progress?: number;
  showCancelButton?: boolean;
  onCancel?: () => void;
}

export const GlobalLoading: React.FC<GlobalLoadingProps> = ({
  isVisible,
  message,
  progress,
  showCancelButton = false,
  onCancel,
}) => {
  if (!isVisible) return null;

  const { t } = useTranslation();

  return (
    <LoadingOverlay isVisible={isVisible} onClick={e => e.stopPropagation()}>
      <ContentWrapper>
        <LoadingText>{message ?? t(i18nKeys.common.loading)}</LoadingText>
        <ProgressWrapper>
          <ProgressTrack>
            {progress !== undefined ? <DeterminateFill value={progress} /> : <IndeterminateBar />}
          </ProgressTrack>
        </ProgressWrapper>
        <LoadingSubText>{t(i18nKeys.common.loading_please_wait)}</LoadingSubText>
        {showCancelButton && onCancel && (
          <>
            <WarningText>⚠️ {t(i18nKeys.common.loading_cancel_warning)}</WarningText>
            <CancelButton onClick={onCancel}>
              {t(i18nKeys.common.loading_cancel_download)}
            </CancelButton>
          </>
        )}
      </ContentWrapper>
    </LoadingOverlay>
  );
};
